/**
 * Runtime backend-environment override for the native iOS shell. A native
 * picker (three-finger double-tap on the login screen, or the Account tab's
 * admin-only Environment row — see ApiEnvironmentPicker in
 * NativeNavPlugin.swift) selects local/staging/prod; NativeNavBridge persists
 * the choice here and reloads, and the override then wins over the build-time
 * REACT_APP_NATIVE_API_ORIGIN at every subsequent boot (nativeApiOrigin.ts).
 */

export type ApiEnvironmentName = "local" | "staging" | "prod";

export interface ApiEnvironmentOverride {
    environment: ApiEnvironmentName;
    /**
     * Full backend origin. Only meaningful for "local", where the user types
     * an explicit address — presets ("staging"/"prod") always resolve their
     * origin live via originForEnvironment() and never persist a snapshot, so
     * a preset's origin can move (e.g. PROD_FALLBACK_ORIGIN changing) without
     * stranding devices that already picked that preset on a dead host.
     */
    origin?: string;
}

export interface ResolvedApiEnvironment {
    environment: ApiEnvironmentName;
    origin: string;
    /**
     * Runtime override, build-time REACT_APP_NATIVE_API_ORIGIN, the prod fallback, or the iOS
     * shell's pin while it hosts unit tests.
     */
    source: "override" | "build" | "default" | "testHost";
}

const STORAGE_KEY = "serverOverride";
const LEGACY_STORAGE_KEY = "apiEnvironmentOverride";

const DEFAULT_LOCAL_ORIGIN = "http://localhost:3001";
const STAGING_ORIGIN = "https://prime-staging.azurewebsites.net";
const PROD_FALLBACK_ORIGIN = "https://app.primepoint.ai";

/**
 * Trim, drop a trailing slash, and collapse an accidentally doubled scheme
 * ("https://https://host" — seen in the field on a stored local override
 * that already carried its scheme and got another prepended). A doubled
 * scheme breaks every WebView request with ERR_NETWORK and leaves the native
 * layer on its prod fallback origin, so every stored/entered origin passes
 * through here.
 */
export function normalizeOrigin(origin: string): string {
    let normalized = origin.trim().replace(/\/$/, "");
    while (/^https?:\/\/https?:\/\//i.test(normalized)) {
        normalized = normalized.replace(/^https?:\/\//i, "");
    }
    return normalized;
}

function buildTimeOrigin(): string | undefined {
    const origin = process.env.REACT_APP_NATIVE_API_ORIGIN?.trim();
    return origin ? normalizeOrigin(origin) : undefined;
}

function originForEnvironment(override: ApiEnvironmentOverride): string {
    switch (override.environment) {
        case "staging":
            return STAGING_ORIGIN;
        case "prod":
            // A runtime Production selection must override the Debug bundle's
            // localhost build origin too. The build-time value is only the
            // default when no picker override exists (resolveApiEnvironment).
            return PROD_FALLBACK_ORIGIN;
        default: {
            // Only "local" takes an explicit origin — presets always resolve
            // live above, ignoring any stored origin (see readEnvironmentOverride).
            const explicitOrigin = override.origin ? normalizeOrigin(override.origin) : undefined;
            return explicitOrigin || DEFAULT_LOCAL_ORIGIN;
        }
    }
}

function isEnvironmentName(value: unknown): value is ApiEnvironmentName {
    return value === "local" || value === "staging" || value === "prod";
}

export function readEnvironmentOverride(): ApiEnvironmentOverride | null {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (raw) {
            const parsed: { choice?: unknown; origin?: unknown } = JSON.parse(raw);
            if (!isEnvironmentName(parsed.choice)) return null;
            if (parsed.choice !== "local") {
                // Presets resolve their origin live via originForEnvironment.
                // A persisted "origin" here is a snapshot from before this fix
                // (or a stale preset pick) that would otherwise pin the device
                // to a dead host forever if the preset's real origin moves —
                // ignore it rather than trust it.
                return { environment: parsed.choice };
            }
            if (typeof parsed.origin !== "string") return null;
            const origin = normalizeOrigin(parsed.origin);
            return origin ? { environment: parsed.choice, origin } : null;
        }

        // One-time compatibility with builds that used a separate native key.
        // The next write migrates it into the current storage shape.
        const legacyRaw = window.localStorage.getItem(LEGACY_STORAGE_KEY);
        if (!legacyRaw) return null;
        const legacy: { environment?: unknown; localOrigin?: unknown } = JSON.parse(legacyRaw);
        if (!isEnvironmentName(legacy.environment)) return null;
        return {
            environment: legacy.environment,
            origin: typeof legacy.localOrigin === "string" ? legacy.localOrigin : undefined,
        };
    } catch {
        return null;
    }
}

export function persistEnvironmentOverride(override: ApiEnvironmentOverride): void {
    try {
        window.localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify(
                // Only "local" needs a persisted origin (the user-typed address).
                // Presets are recomputed live on every read, so a resolved-at-
                // selection-time snapshot never gets baked in and go stale.
                override.environment === "local"
                    ? { choice: override.environment, origin: originForEnvironment(override) }
                    : { choice: override.environment },
            ),
        );
        window.localStorage.removeItem(LEGACY_STORAGE_KEY);
    } catch {
        // localStorage may be unavailable; the switch just won't stick.
    }
}

/**
 * The origin the iOS shell pins while its process is under test (`TestHost.webPageScript`):
 * one that never resolves. Hosting unit tests, the shell's page runs beside them and acts on
 * their fixtures; launched by a UI test, it must not report to production analytics. So none of
 * its requests may reach a real backend, whatever the picker or the build says.
 */
function testHostOrigin(): string | undefined {
    const pinned = (window as { __primepointTestHostApiOrigin?: unknown })
        .__primepointTestHostApiOrigin;
    return typeof pinned === "string" && pinned ? pinned : undefined;
}

/**
 * Whether the iOS shell's process is under test and must report to no Amplitude project
 * (`TestHost.reportsNoAnalytics`, set at document start by `TestHost.webPageScript`): a
 * hosted unit test, a launch with no backend, or a UI-driver launch that reaches staging. A
 * simulator is not a person, so its page must not count as a device in any project.
 */
export function isAnalyticsSilencedByTestHost(): boolean {
    const silenced = (window as { __primepointNoAnalytics?: unknown }).__primepointNoAnalytics;
    return silenced === true || testHostOrigin() !== undefined;
}

/** Whether `origin` is the one a build resolves with no override and no build-time origin
 *  (production): every web build resolves it. */
export function isDefaultApiOrigin(origin: string): boolean {
    return origin === PROD_FALLBACK_ORIGIN;
}

export function resolveApiEnvironment(): ResolvedApiEnvironment {
    const resolved = resolveConfiguredApiEnvironment();
    const pinned = testHostOrigin();
    // Only the origin moves: the environment still names the Stytch project of the keychain
    // session that the test host shares with real launches (StytchAuth.configureEnvironment).
    return pinned ? { ...resolved, origin: pinned, source: "testHost" } : resolved;
}

function resolveConfiguredApiEnvironment(): ResolvedApiEnvironment {
    const override = readEnvironmentOverride();
    if (override) {
        return {
            environment: override.environment,
            origin: originForEnvironment(override),
            source: "override",
        };
    }
    const configured = buildTimeOrigin();
    if (configured) {
        // Three-way match: Debug builds bake in http://localhost:3001, which is
        // neither the staging nor the prod origin. Mislabeling that as "prod"
        // sends a LIVE Stytch token to a localhost origin, breaking OAuth
        // (no_match_for_provided_oauth_url) and misreporting "Production" in
        // the picker. Anything that isn't staging or prod is local.
        const environment: ApiEnvironmentName =
            configured === STAGING_ORIGIN
                ? "staging"
                : configured === PROD_FALLBACK_ORIGIN
                  ? "prod"
                  : "local";
        return { environment, origin: configured, source: "build" };
    }
    // No override and no build-time origin — a fresh TestFlight/App Store
    // install lands here, so it must default to prod, not staging. Devs who
    // want staging/local pick it via the runtime environment switcher.
    return { environment: "prod", origin: PROD_FALLBACK_ORIGIN, source: "default" };
}
